import './bootstrap'
import '../assets/css/app.css'

import { createApp, h } from 'vue'
import { createInertiaApp } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import { InertiaProgress } from '@inertiajs/progress'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from '../../vendor/tightenco/ziggy/dist/vue.m'
import globalcomponents from './GlobalComponents'
import utils from '../assets/utils'
import { createPinia } from 'pinia'
import { createGtm } from '@gtm-support/vue-gtm'
import { applySavedConsent, initCookieConsent } from './utils/cookieConsent'
import { resolveIframeSession, trackIframeSession, TOKEN_HEADER } from './utils/iframeSession'
import { consentInGtmOf, gtmIdOf, needsContainerReload, setLoadedGtmId } from './utils/tracking'
import { i18nVue } from 'laravel-vue-i18n'

// Cookieless session support for iframe (Safari ITP / iOS).
// Read token from meta tag rendered by server, inject into every Inertia + Axios request.
function useIframeToken(iframeToken) {
  // For Inertia requests (form.post, Inertia.visit, etc.)
  Inertia.on('before', (event) => {
    event.detail.visit.headers[TOKEN_HEADER] = iframeToken
  })

  // For direct Axios calls (e.g. locale change)
  window.axios.defaults.headers.common[TOKEN_HEADER] = iframeToken
}

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel'
const appElement = document.getElementById('app')
const documentPage = JSON.parse(appElement.dataset.page)
const documentToken = document.head.querySelector('meta[name="iframe-token"]')?.getAttribute('content') || null

// V iframu bez cookies může dokument přijít bez session (obnovení rámce,
// nasazení nové verze). Aplikace se pak spustí až s obnovenou stránkou, aby
// se z prázdného dokumentu nic nevykreslilo ani nezměřilo.
resolveIframeSession(documentPage, documentToken).then(({ page: initialPage, token }) => {
  if (token) {
    useIframeToken(token)
    trackIframeSession(token)
  }

  if (initialPage !== documentPage) {
    // Obnovená stránka nahradí aktuální záznam historie. Jinak by ji Inertia
    // při jiné adrese přidala jako nový záznam a prázdný dokument by zůstal.
    window.history.replaceState(initialPage, '', initialPage.url)
    // Hlavička stránky předala souhlas kontejneru prázdného dokumentu.
    // Obnovená stránka může patřit jinému provozovateli.
    const resumedGtmId = gtmIdOf(initialPage)
    if (resumedGtmId && !consentInGtmOf(initialPage)) applySavedConsent(resumedGtmId)
  }

  mountApp(initialPage)
})

function mountApp(initialPage) {
  createInertiaApp({
    page: initialPage,
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, app, props, plugin }) {
      const setupApp = createApp({ render: () => h(app, props) })
        .use(plugin)
        .use(ZiggyVue, Ziggy)
        .use(createPinia())
        .use(i18nVue, {
          fallbackLang: 'en',
          resolve: async (lang) => {
            const langs = import.meta.glob('../lang/*.json')
            return await langs[`../lang/${lang}.json`]()
          },
        })

      const loadedGtmId = gtmIdOf(props.initialPage)
      setLoadedGtmId(loadedGtmId)

      if (loadedGtmId) {
        setupApp.use(createGtm({ id: loadedGtmId, debug: false }))
        if (!consentInGtmOf(props.initialPage)) {
          initCookieConsent({
            gtmId: loadedGtmId,
            locale: props.initialPage.props.locale,
            privacyUrls: props.initialPage.props.reservationPage.privacyUrls,
          })
        }
      }

      // Další stránka patří jinému kontejneru (nový průchod u jiného
      // parkoviště). Měřicí události mezitím zablokuje trackingGtm(), celé
      // načtení pak zapne ten správný. V iframu se nenačítá, viz tracking.js.
      Inertia.on('navigate', () => {
        if (needsContainerReload()) {
          window.location.reload()
        }
      })

      const toggleLocale = async (locale) => {
        await axios
          .post(route('locale'), { locale: locale })
          .then((response) => {
            setupApp.use(i18nVue, {
              lang: response.data.props.locale,
            })
          })
          .catch((error) => {
            console.log(error)
          })
      }

      globalcomponents(setupApp)
      utils(setupApp)
      return setupApp.mount(el)
    },
  })
}

InertiaProgress.init({ color: '#4B5563' })
